import { useEffect, useMemo, useState } from 'react';
import { JsonView, collapseAllNested, darkStyles, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/theme/theme-provider';
import type { EnvSchema } from './EnvSelector';
import { COLLECTION_SOURCES, buildR2Url, formatVersion } from './lib/collection-sources';
import { callEdgeFunction } from './supabase.client';

interface ModifiedRow {
  pk?: Record<string, unknown>;
  before?: unknown;
  after?: unknown;
  // Older variant: the EF may also return raw before/after objects without
  // an explicit `pk`. We fall back to the row identity when rendering.
}

interface TableDiff {
  table: string;
  added: number;
  removed: number;
  modified: number;
  added_rows?: unknown[];
  removed_rows?: unknown[];
  modified_rows?: (ModifiedRow | unknown)[];
}

interface DiffResp {
  tables: TableDiff[];
  diff?: string;
  // 이 env 에 아직 승급되지 않아 diff 대상에서 제외된 테이블 (sync_schema 대기).
  skipped?: string[];
}

interface Props {
  env: EnvSchema;
  onClose: () => void;
}

export function DiffPanel({ env, onClose }: Props) {
  const [diffs, setDiffs] = useState<TableDiff[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(false);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    callEdgeFunction<DiffResp>('diff', { schema: env })
      .then((r) => {
        if (r.diff === 'first apply') setFirst(true);
        else {
          setDiffs(r.tables ?? []);
          setSkipped(r.skipped ?? []);
        }
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [env]);

  const changedTables = useMemo(
    () => new Set(diffs.filter((d) => d.added + d.removed + d.modified > 0).map((d) => d.table)),
    [diffs]
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Diff vs last applied ({env})</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {err && <div className="text-sm text-rose-600 dark:text-rose-400">Error: {err}</div>}
        {first && (
          <div className="text-sm text-muted-foreground">
            No previous apply — nothing to diff against.
          </div>
        )}
        {!loading && !err && !first && skipped.length > 0 && (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium">이 env 에 없어 제외된 테이블 ({skipped.length}):</span>{' '}
            <span className="font-mono">{skipped.join(', ')}</span>
            <div className="mt-1">sync_schema 로 구조를 승급하면 diff 대상에 포함됩니다.</div>
          </div>
        )}
        {!loading && !err && !first && (
          <Tabs defaultValue="sources" className="w-full">
            <TabsList>
              <TabsTrigger value="sources">Source tables</TabsTrigger>
              <TabsTrigger value="outputs">Output collections</TabsTrigger>
            </TabsList>
            <TabsContent value="sources">
              <SourceTablesView diffs={diffs} />
            </TabsContent>
            <TabsContent value="outputs">
              <OutputCollectionsView env={env} changedTables={changedTables} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SourceTablesView({ diffs }: { diffs: TableDiff[] }) {
  const changed = diffs.filter((d) => d.added + d.removed + d.modified > 0);
  if (changed.length === 0) {
    return (
      <div className="px-2 py-6 text-sm text-muted-foreground">No changes since last apply.</div>
    );
  }
  return (
    <div className="flex max-h-[60vh] flex-col gap-2 overflow-auto pr-1">
      {changed.map((d) => (
        <SourceTableRow key={d.table} diff={d} />
      ))}
    </div>
  );
}

function SourceTableRow({ diff }: { diff: TableDiff }) {
  const [open, setOpen] = useState(false);
  const total = diff.added + diff.removed + diff.modified;
  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 px-3 py-2 text-left text-sm hover:bg-muted/40"
        aria-expanded={open}
      >
        <span className="font-mono text-foreground">{diff.table}</span>
        <span className="flex items-center gap-3 text-xs">
          {diff.added > 0 && (
            <span className="text-emerald-600 dark:text-emerald-400">+{diff.added}</span>
          )}
          {diff.removed > 0 && (
            <span className="text-rose-600 dark:text-rose-400">−{diff.removed}</span>
          )}
          {diff.modified > 0 && (
            <span className="text-amber-600 dark:text-amber-400">~{diff.modified}</span>
          )}
          <span className="text-muted-foreground">
            ({total} change{total === 1 ? '' : 's'})
          </span>
        </span>
      </button>
      {open && (
        <div className="border-t border-border bg-background px-3 py-2 text-xs">
          {diff.added_rows && diff.added_rows.length > 0 && (
            <DiffSection
              title={`Added (${diff.added_rows.length})`}
              tone="added"
              rows={diff.added_rows}
            />
          )}
          {diff.removed_rows && diff.removed_rows.length > 0 && (
            <DiffSection
              title={`Removed (${diff.removed_rows.length})`}
              tone="removed"
              rows={diff.removed_rows}
            />
          )}
          {diff.modified_rows && diff.modified_rows.length > 0 && (
            <ModifiedSection rows={diff.modified_rows} />
          )}
          {!diff.added_rows && !diff.removed_rows && !diff.modified_rows && (
            <div className="text-muted-foreground">
              Row-level detail not exposed by /diff for this table.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiffSection({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: 'added' | 'removed';
  rows: unknown[];
}) {
  const { mode } = useTheme();
  const colorClass =
    tone === 'added'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';
  return (
    <div className="mt-2 first:mt-0">
      <div className={`mb-1 font-medium ${colorClass}`}>{title}</div>
      <div className="flex flex-col gap-1">
        {rows.map((row, idx) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: diff snapshot from the EF; rows are stable and not reordered
            key={`${tone}-${idx}`}
            className="rounded border border-border bg-muted/30 p-2 font-mono"
          >
            <JsonView
              data={row as object}
              shouldExpandNode={collapseAllNested}
              style={mode === 'dark' ? darkStyles : defaultStyles}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ModifiedSection({ rows }: { rows: unknown[] }) {
  const { mode } = useTheme();
  return (
    <div className="mt-2">
      <div className="mb-1 font-medium text-amber-600 dark:text-amber-400">
        Modified ({rows.length})
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((row, idx) => {
          const m = row as ModifiedRow;
          const before = m.before ?? row;
          const after = m.after ?? row;
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: diff snapshot from the EF; rows are stable and not reordered
              key={`mod-${idx}`}
              className="grid grid-cols-2 gap-2 rounded border border-border bg-muted/30 p-2 font-mono"
            >
              <div>
                <div className="mb-1 text-[10px] uppercase text-muted-foreground">before</div>
                <JsonView
                  data={(before ?? {}) as object}
                  shouldExpandNode={collapseAllNested}
                  style={mode === 'dark' ? darkStyles : defaultStyles}
                />
              </div>
              <div>
                <div className="mb-1 text-[10px] uppercase text-muted-foreground">after</div>
                <JsonView
                  data={(after ?? {}) as object}
                  shouldExpandNode={collapseAllNested}
                  style={mode === 'dark' ? darkStyles : defaultStyles}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OutputCollectionsView({
  env,
  changedTables,
}: {
  env: EnvSchema;
  changedTables: Set<string>;
}) {
  const futureVersion = useMemo(() => formatVersion(new Date()), []);
  const [copied, setCopied] = useState<string | null>(null);
  const entries = useMemo(() => Object.entries(COLLECTION_SOURCES).sort(), []);

  return (
    <div className="flex max-h-[60vh] flex-col gap-2 overflow-auto pr-1">
      <p className="text-xs text-muted-foreground">
        Collections derived from build-json. The R2 URL preview uses the current UTC time formatted
        as YYYYMMDDHHmm — the actual version is assigned at apply time.
      </p>
      {entries.map(([key, sources]) => {
        const willChange = sources.some((t) => changedTables.has(t));
        const url = buildR2Url(env, key, futureVersion);
        return (
          <div key={key} className="rounded-md border border-border bg-card px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm text-foreground">{key}</span>
              {willChange ? (
                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300">
                  변경 예상
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  변경 없음
                </Badge>
              )}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium">Sources:</span>{' '}
              {sources.map((t, idx) => (
                <span key={t}>
                  <span
                    className={`font-mono ${
                      changedTables.has(t)
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {t}
                  </span>
                  {idx < sources.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
            <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="break-all font-mono">{url}</span>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(url);
                  setCopied(key);
                  setTimeout(() => setCopied((c) => (c === key ? null : c)), 1200);
                }}
              >
                {copied === key ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className="text-[10px] text-muted-foreground">
              <span>(예상 — 실제 version은 apply 시점에 할당)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
